/**
 * UpdateManager - Centralized update loop for all per-frame operations
 *
 * Benefits:
 * - Single onBeforeRenderObservable callback instead of many
 * - Built-in throttling for non-critical updates
 * - Easier profiling and debugging
 * - Consistent deltaTime calculation
 *
 * Usage:
 *   const updateManager = UpdateManager.getInstance(scene);
 *
 *   // Register a callback that runs every frame
 *   const id = updateManager.register('mySystem', (deltaTime) => { ... });
 *
 *   // Register a throttled callback (runs every N frames)
 *   const id = updateManager.register('audioUpdate', callback, { throttle: 3 });
 *
 *   // Unregister when done
 *   updateManager.unregister(id);
 */

// Singleton instances per scene
const instances = new WeakMap();

/**
 * Per-frame update ordering. Lower values run first.
 *
 * clickToWalk and tourWalk currently remain at DEFAULT even though they write
 * cameraDirection. That preserves their existing one-frame third-person lag;
 * move them to INPUT in a focused follow-up with behavior coverage.
 */
export const UPDATE_ORDER = {
  INPUT: -200,
  CAMERA_RIG: -100,
  DEFAULT: 0,
  POSE: 100,
};

export class UpdateManager {
  /**
   * Get or create UpdateManager instance for a scene
   * @param {import('@babylonjs/core').Scene} scene
   * @returns {UpdateManager}
   */
  static getInstance(scene) {
    if (!instances.has(scene)) {
      instances.set(scene, new UpdateManager(scene));
    }
    return instances.get(scene);
  }

  /**
   * Clear instance for a scene (call when disposing scene)
   * @param {import('@babylonjs/core').Scene} scene
   */
  static clearInstance(scene) {
    const instance = instances.get(scene);
    if (instance) {
      instance.dispose();
      instances.delete(scene);
    }
  }

  constructor(scene) {
    this.scene = scene;
    this.callbacks = new Map();
    this.nextId = 0;
    this.frameCount = 0;
    this.lastTime = performance.now();

    // Single observer for all updates
    this._observer = scene.onBeforeRenderObservable.add(() => {
      this._update();
    });

    console.log('📊 UpdateManager initialized');
  }

  /**
   * Register an update callback
   * @param {string} name - Name for debugging/profiling
   * @param {Function} callback - Called with seconds of capped wall time since
   *   this callback last ran, plus the global frame count
   * @param {Object} [options] - Optional settings
   * @param {number} [options.throttle] - Run every N frames (default: 1 = every frame)
   * @param {number} [options.priority] - Lower runs first (default: 0)
   * @returns {number} Registration ID for unregistering
   */
  register(name, callback, options = {}) {
    const id = this.nextId++;
    const throttle = options.throttle || 1;
    const priority = options.priority || 0;

    this.callbacks.set(id, {
      name,
      callback,
      throttle,
      priority,
      offset: id % throttle, // Stagger throttled callbacks across frames
      enabled: true,
      accumulatedDt: 0,
    });

    // Re-sort by priority when adding new callback
    this._sortCallbacks();

    const throttleInfo = throttle > 1 ? ` (throttled: every ${throttle} frames)` : '';
    console.log(`📊 Registered update: ${name}${throttleInfo}`);

    return id;
  }

  /**
   * Unregister an update callback
   * @param {number} id - Registration ID returned from register()
   */
  unregister(id) {
    const entry = this.callbacks.get(id);
    if (entry) {
      console.log(`📊 Unregistered update: ${entry.name}`);
      this.callbacks.delete(id);
    }
  }

  /**
   * Enable/disable a callback without unregistering
   * @param {number} id - Registration ID
   * @param {boolean} enabled - Whether to enable
   */
  setEnabled(id, enabled) {
    const entry = this.callbacks.get(id);
    if (entry) {
      if (enabled && !entry.enabled) {
        // Disabled wall time and any partial pre-disable interval are not
        // credited when the callback resumes.
        entry.accumulatedDt = 0;
      }
      entry.enabled = enabled;
    }
  }

  /**
   * Main update loop - called once per frame
   * @private
   */
  _update() {
    const now = performance.now();
    const deltaTime = (now - this.lastTime) / 1000; // Convert to seconds
    this.lastTime = now;
    this.frameCount++;

    // Cap deltaTime to prevent physics explosions after tab switch
    const cappedDeltaTime = Math.min(deltaTime, 0.1);

    // Run all registered callbacks
    for (const entry of this.callbacks.values()) {
      if (!entry.enabled) continue;

      entry.accumulatedDt += cappedDeltaTime;

      // Check throttle - use offset to stagger callbacks
      if (entry.throttle > 1) {
        if ((this.frameCount + entry.offset) % entry.throttle !== 0) {
          continue;
        }
      }

      try {
        const accumulatedDt = entry.accumulatedDt;
        entry.accumulatedDt = 0;
        entry.callback(accumulatedDt, this.frameCount);
      } catch (error) {
        console.error(`UpdateManager: Error in ${entry.name}:`, error);
      }
    }
  }

  /**
   * Sort callbacks by priority
   * @private
   */
  _sortCallbacks() {
    // Convert to array, sort, and rebuild map
    const sorted = [...this.callbacks.entries()].sort((a, b) => a[1].priority - b[1].priority);

    this.callbacks = new Map(sorted);
  }

  /**
   * Get stats for debugging
   * @returns {Object} Stats about registered callbacks
   */
  getStats() {
    const stats = {
      totalCallbacks: this.callbacks.size,
      frameCount: this.frameCount,
      callbacks: [],
    };

    for (const [id, entry] of this.callbacks) {
      stats.callbacks.push({
        id,
        name: entry.name,
        throttle: entry.throttle,
        enabled: entry.enabled,
      });
    }

    return stats;
  }

  /**
   * Cleanup
   */
  dispose() {
    if (this._observer) {
      this.scene.onBeforeRenderObservable.remove(this._observer);
      this._observer = null;
    }
    this.callbacks.clear();
    console.log('📊 UpdateManager disposed');
  }
}

export default UpdateManager;
